/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { TPlayerError } from "../../../videoplayer/types";
import { FailureClassification } from "../enums";
import type { SessionEndReason } from "../enums";
import type { IPlaybackSessionAccumulatorSnapshot } from "../IPlaybackSessionAccumulator";
import type { TStatus } from "../types";
import {
  classifyFailure,
  mapEndReasonToWireCodes,
  mapFailureClassificationToStatus
} from "./PlaybackSessionEndReasonMapper";

/**
 * `status` for `playbackStart` — no session outcome exists yet, so only
 * `failure_classification`/`valid_transaction` (spec-required at start) are
 * populated; the rest are left at their omit-empty defaults.
 */
export const buildStartStatus = (): TStatus => ({
  failureClassification: FailureClassification.SUCCESS,
  validTransaction: "VALID",
  duration: 0,
  endTime: 0,
  endReasonCode: "",
  reason: ""
});

export const buildStatus = (
  accumulator: IPlaybackSessionAccumulatorSnapshot,
  reason: SessionEndReason,
  endTimeEpochSeconds: number,
  terminalCodesJoined: string | undefined,
  transientCodesJoined: string | undefined,
  error?: TPlayerError
): TStatus => {
  const { endReasonCode, reason: reasonText } = mapEndReasonToWireCodes(
    reason,
    error !== undefined ? error.code : undefined
  );
  const failureClassification = classifyFailure(reason, accumulator.hasPlayed);

  return {
    failureClassification,
    validTransaction: "VALID",
    duration: accumulator.durationMs,
    endTime: endTimeEpochSeconds,
    endReasonCode,
    reason: reasonText,
    userTerminalCodes: terminalCodesJoined,
    userFinalCodes: transientCodesJoined,
    errorCode: error !== undefined ? error.code : undefined,
    errorType: error !== undefined ? error.category : undefined,
    errorMessage: error !== undefined ? error.message : undefined,
    statusDisposition: mapFailureClassificationToStatus(failureClassification)
  };
};
